import { useState } from 'react';
import { Button, Badge, ButtonGroup } from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { CopyChip } from '../../components/CopyChip';
import { RelativeTime } from '../../components/RelativeTime';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { OneShotSecretReveal } from '../../components/OneShotSecretReveal';
import { HasPermission } from '../../rbac/HasPermission';
import { useApiKeys, useRevokeApiKey, useRotateApiKey } from './useApiKeys';
import { CreateApiKeyModal } from './CreateApiKeyModal';

/**
 * API-Keys Verwaltung pro Brand.
 *
 * Spec-Quelle: 01_apis/01_core.api.valitype.net/ADMIN/views.md → #/api-keys
 * Routes: /:brandSlug/api-keys
 *
 * Phase-1 Lieferumfang:
 *  - List mit Status, Scopes-Pills, Prefix-Copy
 *  - Create-Modal → OneShotSecretReveal (Plaintext einmalig)
 *  - Rotate → OneShotSecretReveal (neuer Plaintext einmalig)
 *  - Revoke → ConfirmDialog
 */
export function ApiKeysPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingRevoke, setPendingRevoke] = useState(null);
  const [pendingRotate, setPendingRotate] = useState(null);
  const [rotated, setRotated] = useState(null);

  const keys = useApiKeys();
  const revoke = useRevokeApiKey();
  const rotate = useRotateApiKey();

  const rows = keys.data?.keys ?? [];
  const active = rows.filter((k) => k.status === 'active');

  const onRevokeConfirm = async () => {
    if (!pendingRevoke) return;
    await revoke.mutateAsync({ id: pendingRevoke.id });
    setPendingRevoke(null);
  };

  const onRotateConfirm = async () => {
    if (!pendingRotate) return;
    const res = await rotate.mutateAsync({ id: pendingRotate.id });
    setPendingRotate(null);
    setRotated(res);
  };

  return (
    <div>
      <PageHeader
        title="API-Keys"
        subtitle={`${active.length} aktiv · brand-gebunden — Keys aus anderer Brand returnen 401 tenant_mismatch`}
        actions={
          <HasPermission perms={['apiKeys.write']}>
            <Button color="primary" onClick={() => setCreateOpen(true)}>
              + Neuer Key
            </Button>
          </HasPermission>
        }
      />

      <DataTable
        loading={keys.isLoading}
        error={keys.error}
        empty={!rows.length}
        emptyMessage="Noch keine API-Keys in dieser Brand."
        onRetry={() => keys.refetch()}
      >
        <thead>
          <tr>
            <th style={{ width: 70 }}>ID</th>
            <th>Label</th>
            <th style={{ width: 180 }}>Prefix</th>
            <th>Scopes</th>
            <th style={{ width: 110 }}>Status</th>
            <th style={{ width: 130 }}>Last Used</th>
            <th style={{ width: 130 }}>Expires</th>
            <th style={{ width: 130 }}>Erstellt</th>
            <th style={{ width: 140 }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((k) => (
            <tr key={k.id} className={k.status === 'revoked' ? 'opacity-50' : undefined}>
              <td>{k.id}</td>
              <td className="fw-semibold">{k.label}</td>
              <td>
                <CopyChip value={k.prefix} label="Prefix" />
              </td>
              <td>
                <div className="d-flex flex-wrap gap-1">
                  {k.scopes.length === 0 && (
                    <span className="text-muted small">keine Scopes</span>
                  )}
                  {k.scopes.map((s) => (
                    <Badge
                      key={s}
                      color={s === 'saas.sso.mint' ? 'warning' : 'secondary'}
                      pill
                      className="orbit-code-badge"
                    >
                      {s}
                    </Badge>
                  ))}
                </div>
              </td>
              <td>
                <StatusBadge value={k.status} />
              </td>
              <td>
                <RelativeTime iso={k.lastUsedAt} />
              </td>
              <td>{k.expiresAt ? <RelativeTime iso={k.expiresAt} /> : <span className="text-muted">—</span>}</td>
              <td>
                <RelativeTime iso={k.createdAt} />
              </td>
              <td className="text-end">
                {k.status === 'active' && (
                  <HasPermission perms={['apiKeys.write']}>
                    <ButtonGroup size="sm">
                      <Button color="info" outline onClick={() => setPendingRotate(k)}>
                        🔁 Rotate
                      </Button>
                      <Button color="danger" outline onClick={() => setPendingRevoke(k)}>
                        Revoke
                      </Button>
                    </ButtonGroup>
                  </HasPermission>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>

      <CreateApiKeyModal isOpen={createOpen} onClose={() => setCreateOpen(false)} />

      <ConfirmDialog
        isOpen={!!pendingRevoke}
        title="API-Key revoken?"
        body={
          pendingRevoke ? (
            <>
              <p className="mb-2">
                <strong>{pendingRevoke.label}</strong> (<code>{pendingRevoke.prefix}</code>) wird sofort
                ungültig. Calls mit diesem Key bekommen <code>401 revoked_api_key</code>.
              </p>
              <p className="mb-0 small text-muted">
                Revoke ist nicht reversibel. Wenn der Empfänger weiterläuft, erst rotate
                + Empfänger updaten, dann revoke.
              </p>
            </>
          ) : null
        }
        confirmLabel="Revoke"
        confirmColor="danger"
        busy={revoke.isPending}
        onConfirm={onRevokeConfirm}
        onCancel={() => setPendingRevoke(null)}
      />

      <ConfirmDialog
        isOpen={!!pendingRotate}
        title="Secret rotieren?"
        body={
          pendingRotate ? (
            <>
              <p className="mb-2">
                Neuer Plaintext für <strong>{pendingRotate.label}</strong> wird erzeugt — das
                alte Secret ist <strong>sofort tot</strong>. Empfänger muss den neuen Key
                konfigurieren.
              </p>
              <p className="mb-0 small text-muted">
                Prefix-ID bleibt gleich — nur das Plaintext-Secret rotiert.
              </p>
            </>
          ) : null
        }
        confirmLabel="Jetzt rotieren"
        confirmColor="warning"
        busy={rotate.isPending}
        onConfirm={onRotateConfirm}
        onCancel={() => setPendingRotate(null)}
      />

      {rotated && (
        <OneShotSecretReveal
          isOpen
          label="Neuer API-Key Plaintext"
          secret={rotated.plaintext}
          confirmPhrase="Empfänger wurde mit dem neuen Secret konfiguriert"
          onAcknowledged={() => setRotated(null)}
        />
      )}
    </div>
  );
}
